import type { LaborRate, Move } from "@/types";
import { getDay, parseISO } from "date-fns";

export const isLead = (move: Move) => {
  return (
    move.status === "initial" ||
    move.status === "quoted" ||
    move.status === "contacting"
  );
};

export const isBooked = (move: Move) => {
  return (
    move.status === "booked" ||
    move.status === "completed" ||
    move.status === "in_progress"
  );
};

export const computeHourlyRate = (
  numMovers: number,
  laborRates: LaborRate[],
  date: string | Date | null | undefined
) => {
  let dayOfWeek = null;

  if (numMovers <= 0 || !laborRates || laborRates.length === 0) {
    return 0;
  }

  if (date) {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    dayOfWeek = getDay(dateObj);
  }

  // Find the base rate for the crew size (or use 6 if numMovers > 6)
  const baseCrewSize = Math.min(numMovers, 6);
  const baseRate = laborRates.find((rate) => rate.crew_size === baseCrewSize);

  // Find the additional mover rate (crew_size 0)
  const additionalMoverRate = laborRates.find((rate) => rate.crew_size === 0);

  if (!baseRate) {
    return 0; // No matching rate found
  }

  // Determine which rate to use based on day of week
  // dayOfWeek: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  let rateForDay = 0;

  if (dayOfWeek === 0) {
    // Sunday
    rateForDay = baseRate.sun_rate;
  } else if (dayOfWeek === 5) {
    // Friday
    rateForDay = baseRate.fri_rate;
  } else if (dayOfWeek === 6) {
    // Saturday
    rateForDay = baseRate.sat_rate;
  } else {
    // Monday to Thursday (1-4)
    rateForDay = baseRate.mon_thu_rate;
  }

  // If we have more than 6 movers, add the cost for additional movers
  if (numMovers > 6 && additionalMoverRate) {
    const extraMovers = numMovers - 6;
    let additionalRate = 0;

    // Get the appropriate rate for additional movers based on day of week
    if (dayOfWeek === 0) {
      additionalRate = additionalMoverRate.sun_rate;
    } else if (dayOfWeek === 5) {
      additionalRate = additionalMoverRate.fri_rate;
    } else if (dayOfWeek === 6) {
      additionalRate = additionalMoverRate.sat_rate;
    } else {
      additionalRate = additionalMoverRate.mon_thu_rate;
    }

    return rateForDay + extraMovers * additionalRate;
  }

  return rateForDay;
};
